/**
 * ReportsPage Component
 *
 * This component renders a page displaying various reports based on the selected control.
 * It uses the PageLayout component for consistent layout across the application.
 *
 * @component
 */
import {
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  Tabs,
  TabsProps,
  Spinner,
  Flashbar,
  Button,
  Alert,
} from '@cloudscape-design/components';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useControls } from 'src/api/hooks/useControlData';
import reportConfigs, { ReportConfig, ReportPageConfig } from '../../common/config/ReportsConfig';
import PageLayout from '../common/PageLayout/PageLayout';

/**
 * Renders a list of reports for a given tab.
 *
 * @param {ReportConfig[]} reports - Array of report configurations for the tab
 * @param {ReportPageConfig} pageConfig - Overall page configuration
 * @returns {JSX.Element} Rendered reports
 */
const renderReports = (reports: ReportConfig[], pageConfig: ReportPageConfig) => {
  return (
    <SpaceBetween size="m">
      {reports.map((report, index) => {
        if (report.renderAsButton) {
          const dashboardUrl = `https://${pageConfig.region}.quicksight.aws.amazon.com/sn/embed/share/accounts/${pageConfig.accountId}/dashboards/${report.dashboardId}?directory_alias=${pageConfig.directoryAlias}`;
          return (
            <Container key={index}>
              <Button
                variant="primary"
                href={dashboardUrl}
                target="_blank"
                iconAlign="right"
                iconName="external"
              >
                {report.headerText}
              </Button>
            </Container>
          );
        }

        return (
          <Container key={index}>
            <ExpandableSection headerText={report.headerText} defaultExpanded={false}>
              <iframe
                title="iframe"
                width="100%"
                height="1000"
                src={`https://${pageConfig.region}.quicksight.aws.amazon.com/sn/embed/share/accounts/${pageConfig.accountId}/dashboards/${report.dashboardId}?directory_alias=${pageConfig.directoryAlias}`}
              />
            </ExpandableSection>
          </Container>
        );
      })}
    </SpaceBetween>
  );
};

/**
 * Main ReportsPage component.
 *
 * @returns {JSX.Element} Rendered ReportsPage
 */
export const ReportsPage: React.FC = () => {
  // Get the list of controls using React Query hook
  const { data, isLoading, error } = useControls();
  const controls = data?.controls ?? [];

  // Determine the report type based on the controlId
  const { controlId } = useParams<{ controlId: string }>();
  const reportType = controlId?.toUpperCase() as keyof typeof reportConfigs;

  // Find the control matching the controlId
  const control = useMemo(() => {
    return controls.find((c) => c.controlId === controlId) || null;
  }, [controls, controlId]);

  const pageConfig = reportConfigs[reportType];

  // Handle case where tabs might be undefined
  const hasTabs = pageConfig.tabs !== undefined && pageConfig.tabs.length > 0;
  const [activeTabId, setActiveTabId] = useState(
    hasTabs && pageConfig.tabs !== undefined ? pageConfig.tabs[0].id : ''
  );

  // Generate tabs based on the page configuration if tabs exist
  const tabs: TabsProps.Tab[] =
    hasTabs && pageConfig.tabs !== undefined
      ? pageConfig.tabs.map((tab) => ({
          label: tab.label,
          id: tab.id,
          content: (
            <SpaceBetween size="m">
              {reportType === 'REPORT1-PAGE0' && (
                <Alert>
                  Amazon Quick Suite Account Name is <strong>fassot-reports</strong>. Reach out to
                  consumer-controllership-dev@amazon.com for fassot-reports access
                </Alert>
              )}
              {renderReports(tab.reports, pageConfig)}
            </SpaceBetween>
          ),
        }))
      : [];

  if (isLoading) {
    return (
      <PageLayout>
        <Spinner />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Flashbar
          items={[
            {
              type: 'error',
              content: `Error loading controls: ${error.message}`,
              dismissible: true,
              onDismiss: () => {},
            },
          ]}
        />
      </PageLayout>
    );
  }

  if (!control) {
    return (
      <PageLayout>
        <Flashbar
          items={[
            {
              type: 'warning',
              content: `No control found for ID: ${controlId}`,
              dismissible: true,
              onDismiss: () => {},
            },
          ]}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      defaultPadding={true}
      header={
        <Header variant="h2" description="">
          {control?.controlSubTitle}
        </Header>
      }
    >
      {hasTabs ? (
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={tabs}
        />
      ) : (
        // If no tabs, display title and render all reports directly
        <SpaceBetween size="m">
          {reportType === 'REPORT1-PAGE0' && (
            <Alert>
              Amazon Quick Suite Account Name is <strong>fassot-reports</strong>. Reach out to
              consumer-controllership-dev@amazon.com for fassot-reports access
            </Alert>
          )}
          {pageConfig.reports && renderReports(pageConfig.reports, pageConfig)}
        </SpaceBetween>
      )}
    </PageLayout>
  );
};
